import { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import type { Me } from '@/lib/types';
import { Avatar } from '@/components/Avatar';

interface Props {
  me: Me | undefined;
  onNavigate?: ((view: 'dashboard' | 'config') => void) | undefined;
}

export function UserMenu({ me, onNavigate }: Props) {
  const { instance } = useMsal();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  const displayName = me?.name ?? account?.name ?? account?.username ?? '—';
  const upn = me?.upn ?? account?.username ?? '';

  const handleLogout = () => {
    void instance.logoutRedirect({
      account: account ?? null,
      postLogoutRedirectUri: window.location.origin,
    });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-3 border border-transparent px-2 py-1.5 text-left transition-colors hover:border-ink-800"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar upn={upn} fullName={displayName} size="sm" />
        <div className="hidden sm:block">
          <div className="text-sm text-ink-100">{displayName}</div>
          <div className="text-[0.7rem] text-ink-500">{upn}</div>
        </div>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          className={`text-ink-500 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <path d="M1 3l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>

      {open ? (
        <div
          role="menu"
          className="panel absolute right-0 z-10 mt-2 w-64 p-1 shadow-2xl shadow-black/40"
        >
          <div className="border-b border-ink-800 px-3 py-3">
            <div className="text-sm text-ink-100">{displayName}</div>
            <div className="mt-1 break-all text-xs text-ink-500">{upn}</div>
            {me ? (
              <div className="mt-3 flex items-center gap-2">
                <span
                  className={`inline-block h-1.5 w-1.5 rounded-full ${
                    me.is_app_admin ? 'bg-emerald-500' : 'bg-ink-600'
                  }`}
                />
                <span className="text-[0.7rem] uppercase tracking-widest text-ink-400">
                  {me.is_app_admin ? 'App admin' : 'Manager'}
                </span>
                <span className="ml-auto text-[0.7rem] text-ink-500">
                  {me.business_lines.length} filiales
                </span>
              </div>
            ) : null}
          </div>
          {me?.is_app_admin && onNavigate ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onNavigate('config');
              }}
              className="block w-full px-3 py-2 text-left text-sm text-ink-200 transition-colors hover:bg-ink-800"
            >
              Configuración
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="block w-full px-3 py-2 text-left text-sm text-ink-200 transition-colors hover:bg-ink-800"
          >
            Cerrar sesión
          </button>
        </div>
      ) : null}
    </div>
  );
}
